import type { ChangeEvent, ReactNode } from "react";

import { TextField, type TextFieldProps } from "@mui/material";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from "react-hook-form";

import { ElementLayout } from "./ElementLayout";

export type BaseTextFieldElementProps<
  FormValues extends FieldValues,
  FieldName extends FieldPath<FormValues> = FieldPath<FormValues>,
> = Omit<TextFieldProps, "error" | "label" | "name" | "onChange" | "value"> & {
  children?: ReactNode;
  fieldName: FieldName;
  getValueFromEvent: (
    event: ChangeEvent<HTMLInputElement>,
  ) => FieldPathValue<FormValues, FieldName> | null;
  isValueChangeCancelled?: (
    value: FieldPathValue<FormValues, FieldName> | null,
  ) => boolean;
  label?: string;
  modifyInputDisplayValue?: (
    value?: FieldPathValue<FormValues, FieldName>,
  ) => FieldPathValue<FormValues, FieldName> | undefined;
  onBlur?: () => void;
  onChange?: (
    value: FieldPathValue<FormValues, FieldName> | null,
    event: ChangeEvent<HTMLInputElement>,
  ) => void;
  rules?: ControllerProps<FormValues, FieldName>["rules"];
  transformValue?: (
    value: FieldPathValue<FormValues, FieldName>,
  ) => FieldPathValue<FormValues, FieldName> | null;
};

export const BaseTextFieldElement = <
  FormValues extends FieldValues,
  FieldName extends FieldPath<FormValues> = FieldPath<FormValues>,
>({
  children,
  fieldName,
  getValueFromEvent,
  isValueChangeCancelled,
  modifyInputDisplayValue,
  rules,
  transformValue,
  ...textFieldProps
}: BaseTextFieldElementProps<FormValues, FieldName>) => {
  return (
    <ElementLayout
      fieldName={fieldName}
      fullWidth={textFieldProps.fullWidth}
      required={textFieldProps.required}
    >
      <Controller<FormValues, FieldName>
        name={fieldName}
        render={({ field, fieldState: { error } }) => {
          const { ref, ...controllerFieldProps } = field;
          return (
            <TextField
              {...textFieldProps}
              {...controllerFieldProps}
              error={error !== undefined}
              helperText={error?.message || textFieldProps.helperText}
              id={fieldName}
              inputRef={ref}
              onBlur={() => {
                controllerFieldProps.onBlur();
                textFieldProps.onBlur?.();
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = getValueFromEvent(event);
                if (isValueChangeCancelled?.(value)) {
                  return;
                }
                const transformedValue =
                  transformValue && value !== null
                    ? transformValue(value)
                    : value;

                textFieldProps.onChange?.(transformedValue, event);
                controllerFieldProps.onChange(transformedValue);
              }}
              value={
                modifyInputDisplayValue
                  ? modifyInputDisplayValue(field.value)
                  : (field.value ?? "")
              }
            >
              {children}
            </TextField>
          );
        }}
        rules={rules}
      />
    </ElementLayout>
  );
};
